// Tournament persistence client (admin side).
//
// Talks to the CaddieGames tournament endpoints. All calls fail soft — a
// network blip returns an empty/false result rather than throwing, matching
// the connections.ts client style.

import { Tournament } from "./tournament";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/caddiegames`;

// The caller's own tournaments, newest first.
export async function listTournaments(): Promise<Tournament[]> {
  try {
    const res = await fetch(`${BASE}/tournaments`, { credentials: "include" });
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body.tournaments)
      ? (body.tournaments as Tournament[])
      : [];
  } catch {
    return [];
  }
}

export type SaveResult =
  | { ok: true; tournament: Tournament }
  | { ok: false; error: string };

// Create or update a tournament (the client owns the id). Returns the saved
// tournament, or a descriptive error the caller MUST surface — a silent
// failure here previously let a tournament look created while never being
// persisted (it vanished on the next load and never reached players).
export async function saveTournament(t: Tournament): Promise<SaveResult> {
  try {
    const res = await fetch(
      `${BASE}/tournaments/${encodeURIComponent(t.id)}`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(t),
      },
    );
    if (!res.ok) {
      let detail = "";
      try {
        const body = await res.json();
        if (body && typeof body.error === "string") detail = body.error;
      } catch {
        // non-JSON error body — status alone is the signal
      }
      return {
        ok: false,
        error: `Server returned ${res.status}${detail ? ` — ${detail}` : ""}`,
      };
    }
    const body = await res.json();
    if (!body?.tournament) {
      return { ok: false, error: "Server did not return the saved tournament" };
    }
    return { ok: true, tournament: body.tournament as Tournament };
  } catch (err) {
    return {
      ok: false,
      error: (err as Error)?.message || "Network error — could not reach the server",
    };
  }
}

export async function deleteTournament(id: string): Promise<boolean> {
  try {
    const res = await fetch(
      `${BASE}/tournaments/${encodeURIComponent(id)}`,
      { method: "DELETE", credentials: "include" },
    );
    return res.ok;
  } catch {
    return false;
  }
}
